import find from 'lodash/find';
import React, { FC, useCallback } from 'react';

import ConditionRow from './ConditionRow';
import { InteractionType, MessageChannelCondition as MessageChannelConditionType } from 'src/actions/rules/condition';
import { StyledDropdown } from 'src/presentation/components/pages/rules/shared/StyledComponents';
import { TriggerType } from 'src/actions/rules/trigger';
import useActiveCustomChannels from '../hooks/useActiveCustomChannels';

type MessageChannelConditionProps = {
  condition: MessageChannelConditionType;
  triggerType: TriggerType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const MessageChannelCondition: FC<MessageChannelConditionProps> = ({
  condition,
  triggerType,
  index,
  isReadOnly,
  onDelete,
  onUpdate,
}) => {
  const onChannelChange = useCallback(
    val => {
      condition.updateValue(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const { loadingState: customChannelLoadingState, values: customChannels } = useActiveCustomChannels();

  if (customChannelLoadingState === 'loading') return null;

  const messagingChannelOptions = customChannels
    .map(c => ({ label: c.name, value: `${InteractionType.CustomChannel}:${c.id}` }))
    .concat(baseChannelOptions);

  if (triggerType !== TriggerType.SmartMatch) {
    messagingChannelOptions.push(...additionalAutomationChannelOptions);
  }

  messagingChannelOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  return (
    <ConditionRow index={index} isReadOnly={isReadOnly} onDelete={onDelete} text="Channel is">
      {isReadOnly ? (
        <span data-aid="readOnlyMessageChannelConditionValues">
          {renderSelectedChannels(condition.value, messagingChannelOptions)}
        </span>
      ) : (
        <StyledDropdown
          data-aid="messageChannelConditionDropdown"
          isMultiSelect
          maxRows={5}
          multiSelectLabel="channels"
          onSelect={onChannelChange}
          options={messagingChannelOptions}
          placeholder="Select channels"
          searchable
          value={condition.value}
        />
      )}
    </ConditionRow>
  );

  function renderSelectedChannels(channels, messagingChannelOptions) {
    return channels.map(channel => find(messagingChannelOptions, { value: channel }).label).join(', ');
  }
};

// Channel Options
export const baseChannelOptions: { label: string; value: string }[] = [
  { label: 'Chat', value: InteractionType.Chat },
  { label: 'Email', value: InteractionType.Email },
  { label: 'Facebook Messenger', value: InteractionType.FbMessenger },
  { label: 'Instagram Comment', value: InteractionType.InstagramComment },
  { label: 'Instagram Messaging', value: InteractionType.InstagramDirect },
  { label: 'SMS', value: InteractionType.Sms },
  { label: 'Voicemail', value: InteractionType.Voicemail },
  { label: 'WhatsApp', value: InteractionType.WhatsApp },
];

const additionalAutomationChannelOptions: { label: string; value: string }[] = [
  { label: 'Phone Call', value: InteractionType.PhoneCall },
];

export default MessageChannelCondition;
